import { useEffect, useState, type FormEvent } from "react";
import {
  orders as api,
  type Order,
  type OrderCheck,
  type OrderEvent,
  type OrderStatus,
} from "../../api";
import { Link } from "../../router";
import {
  formatCurrency,
  formatCurrencyExact,
  formatUnits,
} from "../portfolio/format";
import { quantityLabel, sideLabel, statusLabel, formatDate } from "./format";

interface Action {
  to: OrderStatus;
  label: string;
  kind: string;
  // Opens an input dialog instead of transitioning straight away.
  dialog?: "fill" | "reject";
}

// Buttons offered per status, mirroring the server-side transition rules.
const ACTIONS: Record<OrderStatus, Action[]> = {
  draft: [
    { to: "submitted", label: "Submit", kind: "btn-primary" },
    { to: "cancelled", label: "Cancel order", kind: "btn-ghost" },
  ],
  submitted: [
    { to: "executed", label: "Execute", kind: "btn-primary" },
    { to: "partially_filled", label: "Partial fill…", kind: "btn-ghost", dialog: "fill" },
    { to: "rejected", label: "Reject…", kind: "btn-ghost", dialog: "reject" },
    { to: "cancelled", label: "Cancel order", kind: "btn-ghost" },
  ],
  partially_filled: [
    { to: "executed", label: "Fill remainder", kind: "btn-primary" },
    { to: "partially_filled", label: "Partial fill…", kind: "btn-ghost", dialog: "fill" },
    { to: "rejected", label: "Reject remainder…", kind: "btn-ghost", dialog: "reject" },
    { to: "cancelled", label: "Cancel remainder", kind: "btn-ghost" },
  ],
  executed: [],
  cancelled: [],
  rejected: [],
};

export function OrderDetail({ id }: { id: number }) {
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<"fill" | "reject" | "override" | null>(null);
  const [fillPct, setFillPct] = useState("");
  const [reason, setReason] = useState("");
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [checks, setChecks] = useState<OrderCheck[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(id)
      .then((d) => {
        if (cancelled) return;
        setOrder(d.order);
        if (d.order.status === "draft") {
          api
            .checks(id)
            .then((r) => !cancelled && setChecks(r.checks))
            .catch(() => {});
        }
      })
      .catch((e) => !cancelled && setError(e.message));
    api
      .events(id)
      .then((d) => !cancelled && setEvents(d.events))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function transition(
    to: OrderStatus,
    opts: { fillPercent?: number; reason?: string; overrideReason?: string } = {},
  ) {
    setError(null);
    setBusy(true);
    try {
      const { order } = await api.setStatus(id, to, opts);
      setOrder(order);
      setDialog(null);
      setFillPct("");
      setReason("");
      if (order.status !== "draft") setChecks(null);
      api
        .events(id)
        .then((d) => setEvents(d.events))
        .catch(() => {});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function submitDialog(e: FormEvent) {
    e.preventDefault();
    if (dialog === "fill") {
      const pct = Number(fillPct);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        setError("Fill percentage must be between 0 and 100.");
        return;
      }
      void transition("partially_filled", { fillPercent: pct });
    } else if (dialog === "reject") {
      void transition("rejected", { reason: reason.trim() || undefined });
    } else if (dialog === "override") {
      if (!reason.trim()) {
        setError("An override needs a reason — it goes on the order's record.");
        return;
      }
      void transition("submitted", { overrideReason: reason.trim() });
    }
  }

  const checksBlock = checks?.some((c) => c.outcome === "block") ?? false;
  const checksWarn = checks?.some((c) => c.outcome === "warn") ?? false;

  // Submit routes through the execution gate: blocked orders can't go,
  // warnings need an override-with-reason dialog.
  function handleAction(a: Action) {
    if (a.to === "submitted" && checksWarn && !checksBlock) {
      setDialog("override");
      return;
    }
    if (a.dialog) setDialog(a.dialog);
    else void transition(a.to);
  }

  if (error && !order) {
    return (
      <div className="page">
        <Link to="/app/orders" className="back-link">
          ← Orders
        </Link>
        <div className="banner-error">{error}</div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="page">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  const cur = order.account_currency;
  const actions = ACTIONS[order.status];
  const hasFills = order.executed_units != null && order.executed_units > 0;
  const fillPctOfTarget =
    order.target_units && order.executed_units
      ? Math.round((order.executed_units / order.target_units) * 100)
      : null;

  return (
    <div className="page">
      <Link to="/app/orders" className="back-link">
        ← Orders
      </Link>

      <header className="page-head">
        <div>
          <span className="eyebrow">Order #{order.id}</span>
          <h1>
            <span className={`pill side-${order.side}`}>
              {sideLabel(order.side)}
            </span>
            {order.side === "switch"
              ? `${order.instrument_name} → ${order.to_instrument_name}`
              : order.instrument_name}{" "}
            <span className={`pill status-${order.status}`}>
              {statusLabel(order.status)}
            </span>
          </h1>
        </div>
        <div className="page-head-actions">
          <a
            href={`/api/orders/${order.id}/confirmation.pdf`}
            target="_blank"
            rel="noopener"
            className="btn btn-ghost"
          >
            {order.status === "executed"
              ? "Settlement confirmation (PDF)"
              : "Order confirmation (PDF)"}
          </a>
        </div>
      </header>

      {error && <div className="banner-error">{error}</div>}

      <dl className="info-grid">
        <div className="info-row">
          <dt>Investor</dt>
          <dd>
            <Link to={`/app/portfolio/${order.investor_id}`} className="link-action">
              {order.investor_name}
            </Link>
          </dd>
        </div>
        <div className="info-row">
          <dt>Account</dt>
          <dd>
            <Link
              to={`/app/portfolio/accounts/${order.account_id}`}
              className="link-action"
            >
              {order.account_name} ({cur})
            </Link>
          </dd>
        </div>
        <div className="info-row">
          <dt>Instrument</dt>
          <dd className="mono">{order.isin}</dd>
        </div>
        {order.side === "switch" && (
          <div className="info-row">
            <dt>Switch into</dt>
            <dd className="mono">{order.to_isin}</dd>
          </div>
        )}
        <div className="info-row">
          <dt>Quantity</dt>
          <dd>{quantityLabel(order.quantity_mode, order.quantity, cur)}</dd>
        </div>
        {order.status === "partially_filled" &&
          order.target_units != null &&
          order.executed_units != null && (
            <div className="info-row">
              <dt>Fill progress</dt>
              <dd>
                {formatUnits(order.executed_units)} of{" "}
                {formatUnits(order.target_units)} units
                {fillPctOfTarget != null && ` (${fillPctOfTarget}%)`}
              </dd>
            </div>
          )}
        {order.limit_price != null && (
          <div className="info-row">
            <dt>Execution price</dt>
            <dd>{formatCurrencyExact(order.limit_price, cur)}</dd>
          </div>
        )}
        {order.status_reason && (
          <div className="info-row">
            <dt>Reason</dt>
            <dd>{order.status_reason}</dd>
          </div>
        )}
        <div className="info-row">
          <dt>Source</dt>
          <dd>{order.source === "quantfolio" ? "Quantfolio Platform" : "Manual"}</dd>
        </div>
        {order.note && (
          <div className="info-row">
            <dt>Note</dt>
            <dd>{order.note}</dd>
          </div>
        )}
        <div className="info-row">
          <dt>Created</dt>
          <dd>{formatDate(order.created_at)}</dd>
        </div>
        {order.executed_at && (
          <div className="info-row">
            <dt>Executed</dt>
            <dd>{formatDate(order.executed_at)}</dd>
          </div>
        )}
      </dl>

      {hasFills && (
        <>
          <h2 className="section-title">
            {order.status === "executed"
              ? "Applied to portfolio"
              : "Fills applied to portfolio so far"}
          </h2>
          <table className="table">
            <thead>
              <tr>
                <th>Leg</th>
                <th>Instrument</th>
                <th className="num">Units</th>
                <th className="num">Avg price</th>
                <th className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="strong">
                  {order.side === "buy" ? "Bought" : "Sold"}
                </td>
                <td>{order.instrument_name}</td>
                <td className="num">{formatUnits(order.executed_units!)}</td>
                <td className="num">
                  {order.executed_price != null
                    ? formatCurrencyExact(order.executed_price, cur)
                    : "—"}
                </td>
                <td className="num strong">
                  {order.executed_value != null
                    ? formatCurrency(order.executed_value, cur)
                    : "—"}
                </td>
              </tr>
              {order.side === "switch" && order.executed_to_units != null && (
                <tr>
                  <td className="strong">Bought</td>
                  <td>{order.to_instrument_name}</td>
                  <td className="num">{formatUnits(order.executed_to_units)}</td>
                  <td className="num">
                    {order.executed_to_price != null
                      ? formatCurrencyExact(order.executed_to_price, cur)
                      : "—"}
                  </td>
                  <td className="num strong">
                    {order.executed_to_price != null
                      ? formatCurrency(
                          order.executed_to_units * order.executed_to_price,
                          cur,
                        )
                      : "—"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {events.length > 0 && (
        <>
          <h2 className="section-title">History</h2>
          <ol className="timeline">
            {events.map((e) => (
              <li key={e.id} className="timeline-item">
                <span className="timeline-dot" aria-hidden />
                <div className="timeline-body">
                  <div className="timeline-head">
                    <strong>{eventLabel(e.event)}</strong>
                    <span className="muted">
                      {e.actor !== "system" && e.actor !== "backfill"
                        ? `by ${e.actor} · `
                        : ""}
                      {formatDate(e.created_at)}
                    </span>
                  </div>
                  {eventDetail(e, cur) && (
                    <div className="muted">{eventDetail(e, cur)}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </>
      )}

      {order.status === "draft" && checks && (
        <>
          <h2 className="section-title">Pre-trade checks</h2>
          <ul className="check-list">
            {checks.map((c) => (
              <li key={c.check} className={`check-row check-${c.outcome}`}>
                <span className="check-icon" aria-hidden>
                  {c.outcome === "pass" ? "✓" : c.outcome === "warn" ? "!" : "✕"}
                </span>
                <div>
                  <strong>{c.label}</strong>
                  <div className="muted">{c.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {actions.length > 0 && (
        <div className="form-actions">
          {actions.map((a) => {
            const gated = a.to === "submitted" && checksBlock;
            return (
              <button
                key={a.label}
                type="button"
                className={`btn ${a.kind}`}
                disabled={busy || gated}
                title={gated ? "Blocked by pre-trade checks." : undefined}
                onClick={() => handleAction(a)}
              >
                {gated
                  ? "Blocked by checks"
                  : busy && !a.dialog
                    ? "Working…"
                    : a.label}
              </button>
            );
          })}
        </div>
      )}

      {dialog && (
        <div className="modal-backdrop" onClick={() => setDialog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>
              {dialog === "fill"
                ? "Partial fill"
                : dialog === "reject"
                  ? "Reject order"
                  : "Override pre-trade warnings"}
            </h2>
            {dialog === "override" && (
              <p className="muted">
                {checks
                  ?.filter((c) => c.outcome === "warn")
                  .map((c) => c.detail)
                  .join(" ")}
              </p>
            )}
            <form onSubmit={submitDialog}>
              {dialog === "fill" ? (
                <label className="field">
                  <span>Filled share of the total order (%)</span>
                  <input
                    type="number"
                    min="0.01"
                    max="100"
                    step="any"
                    value={fillPct}
                    onChange={(e) => setFillPct(e.target.value)}
                    autoFocus
                    required
                  />
                  <span className="field-hint">
                    Fills accumulate — reaching 100% completes the order.
                  </span>
                </label>
              ) : (
                <label className="field">
                  <span>
                    {dialog === "override" ? "Override reason *" : "Reason (optional)"}
                  </span>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                      dialog === "override"
                        ? "e.g. Client confirmed the duplicate order by phone"
                        : "e.g. Instrument not tradable at counterparty"
                    }
                    autoFocus
                  />
                </label>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setDialog(null)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn ${dialog === "reject" ? "btn-danger" : "btn-primary"}`}
                  disabled={busy}
                >
                  {busy
                    ? "Working…"
                    : dialog === "fill"
                      ? "Apply fill"
                      : dialog === "reject"
                        ? "Reject order"
                        : "Override & submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const EVENT_LABELS: Record<string, string> = {
  created: "Order created",
  submitted: "Sent to execution",
  partial_fill: "Partial fill",
  executed: "Executed",
  cancelled: "Cancelled",
  rejected: "Rejected",
  funding_pull: "Funding pulled from bank account",
};

function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

function eventDetail(e: OrderEvent, currency: string): string | null {
  const p = e.payload;
  if (!p) return null;
  const parts: string[] = [];
  if (typeof p.units === "number") {
    parts.push(
      `${formatUnits(p.units)} units @ ${formatCurrencyExact(Number(p.price ?? 0), currency)}`,
    );
  }
  if (typeof p.value === "number") {
    parts.push(formatCurrency(p.value, currency));
  }
  if (typeof p.amount === "number") {
    parts.push(formatCurrency(p.amount, currency));
  }
  if (typeof p.fill_percent === "number") {
    parts.push(`${p.fill_percent}% of order`);
  }
  if (typeof p.reason === "string" && p.reason) {
    parts.push(p.reason);
  }
  if (typeof p.override_reason === "string" && p.override_reason) {
    parts.push(`Warnings overridden: ${p.override_reason}`);
  }
  if (Array.isArray(p.checks)) {
    const flagged = (p.checks as { check: string; outcome: string }[]).filter(
      (c) => c.outcome !== "pass",
    );
    parts.push(
      flagged.length === 0
        ? "All pre-trade checks passed"
        : `Checks: ${flagged.map((c) => `${c.check} ${c.outcome}`).join(", ")}`,
    );
  }
  return parts.length ? parts.join(" · ") : null;
}
